// tabsRenderConfig.ts
import { organizerConfig } from "../../../../../utils/configs/organizerConfig";
import DynamicPage from "../../Dynamic/DynamicPage";

/**
 * Function to render custom content for a tab.
 * @callback RenderFunction
 * @param {Record<string, any>} responses - The user's responses stored in the Zustand store.
 * @returns {JSX.Element} JSX element representing the custom content.
 */
type RenderFunction = (responses: Record<string, any>) => JSX.Element;

/**
 * Function to render a page using the organizerConfig.
 * @callback RenderPageFunction
 * @param {string} step - The current step name.
 * @param {string} page - The current page name.
 * @param {Record<string, any>} responses - The user's responses stored in the Zustand store.
 * @param {number | undefined} taxYear - The current tax year.
 * @returns {JSX.Element} JSX element representing the page content.
 */
type RenderPageFunction = (
  step: string,
  page: string,
  responses: Record<string, any>,
  taxYear?: number
) => JSX.Element;

/**
 * Interface for the tabsRenderConfig.
 * Each key represents a step, with an optional custom render function and a renderPage function.
 * @typedef {Object} TabsRenderConfig
 * @property {RenderFunction} [render] - Optional custom render function for the step.
 * @property {RenderPageFunction} [renderPage] - Optional renderPage function for the step.
 */
interface TabsRenderConfig {
  [key: string]: {
    render?: RenderFunction;
    renderPage?: RenderPageFunction;
  };
}

/**
 * Configuration for rendering different tabs in the ReviewPage component.
 * Each entry in this configuration specifies how to render content for a particular step.
 * - `Getting Started`: Uses a custom render function to display user responses in a simple format.
 * - `default`: Uses a renderPage function to render content based on the page configuration in organizerConfig.
 * @type {TabsRenderConfig}
 */
export const tabsRenderConfig: TabsRenderConfig = {
  default: {
    /**
     * Render page function for steps other than "Getting Started".
     * Renders content based on the page configuration in organizerConfig.
     *
     * @param {string} step - The current step name.
     * @param {string} page - The current page name.
     * @param {Record<string, any>} _responses - The user's responses stored in the Zustand store (unused).
     * @param {number | undefined} taxYear - The current tax year.
     * @returns {JSX.Element} JSX element representing the page content.
     */
    renderPage: (step, page, _responses, taxYear) => {
      const pageConfig = organizerConfig[step]?.pages?.[page];
      if (pageConfig) {
        const { title, ...restConfig } = pageConfig;
        return (
          <div key={page} className="review-item">
            <DynamicPage
              pageConfig={restConfig}
              review={true}
              taxYear={taxYear!}
            />
          </div>
        );
      } else {
        return (
          <div key={page} className="review-item">
            Invalid page configuration
          </div>
        );
      }
    },
  },
};
